import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Settings2, Pencil, Check, X, Trash2, ChevronUp, ChevronDown, Tags } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { tipo: "grau", label: "Graus" },
  { tipo: "instituicao", label: "Instituições" },
  { tipo: "unidade", label: "Unidades" },
  { tipo: "pais", label: "Países" },
];

export function ListaConfigDialog({ onChanged }) {
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState({ grau: [], instituicao: [], unidade: [], pais: [] });
  const [novo, setNovo] = useState({ grau: "", instituicao: "", unidade: "", pais: "" });
  const [editId, setEditId] = useState(null);
  const [editNome, setEditNome] = useState("");
  const [delTarget, setDelTarget] = useState(null); // {id, nome}
  const [aliasId, setAliasId] = useState(null);
  const [aliasText, setAliasText] = useState("");

  const loadAll = () => {
    TABS.forEach(({ tipo }) =>
      api.get(`/listas/${tipo}?incluir_inativas=true`).then((r) => setItems((s) => ({ ...s, [tipo]: r.data })))
    );
  };
  const add = async (tipo) => {
    const nome = novo[tipo].trim();
    if (!nome) return;
    await api.post(`/listas/${tipo}`, { nome });
    setNovo((s) => ({ ...s, [tipo]: "" }));
    loadAll();
    onChanged && onChanged();
    toast.success("Item adicionado");
  };
  const toggle = async (tipo, it) => {
    if (it.ativo) await api.delete(`/listas/item/${it.id}`);
    else await api.put(`/listas/item/${it.id}`, { ativo: true });
    loadAll();
    onChanged && onChanged();
  };
  const startEdit = (it) => { setEditId(it.id); setEditNome(it.nome); };
  const cancelEdit = () => { setEditId(null); setEditNome(""); };
  const saveEdit = async (it) => {
    const nome = editNome.trim();
    if (!nome) return;
    await api.put(`/listas/item/${it.id}`, { nome });
    cancelEdit();
    loadAll();
    onChanged && onChanged();
    toast.success("Item atualizado");
  };
  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/listas/item/${delTarget.id}/permanent`);
      toast.success("Item excluído definitivamente");
      setDelTarget(null);
      loadAll();
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setDelTarget(null); }
  };
  const moveGrau = async (arr, idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    const a = arr[idx], b = arr[j];
    const ha = a.hier ?? idx, hb = b.hier ?? j;
    await api.put(`/listas/item/${a.id}`, { hier: hb });
    await api.put(`/listas/item/${b.id}`, { hier: ha });
    loadAll(); onChanged && onChanged();
  };
  const startAlias = (it) => { setAliasId(it.id); setAliasText((it.aliases || []).join(", ")); };
  const saveAlias = async (it) => {
    const aliases = aliasText.split(",").map((s) => s.trim()).filter(Boolean);
    await api.put(`/listas/item/${it.id}`, { aliases });
    setAliasId(null); setAliasText(""); loadAll(); onChanged && onChanged();
    toast.success("Equivalências atualizadas");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) loadAll(); }}>
      <DialogTrigger asChild>
        <Button variant="secondary" data-testid="config-listas-btn"><Settings2 className="h-4 w-4 mr-2" />Configurar listas</Button>
      </DialogTrigger>
      <DialogContent className="bg-popover border-border w-[calc(100vw-1.5rem)] max-w-lg overflow-hidden">
        <DialogHeader><DialogTitle>Listas configuráveis</DialogTitle></DialogHeader>
        <Tabs defaultValue="grau" className="w-full min-w-0">
          <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full h-auto gap-1">
            {TABS.map((t) => <TabsTrigger key={t.tipo} value={t.tipo} data-testid={`tab-${t.tipo}`} className="text-xs sm:text-sm px-1.5 py-1.5 whitespace-normal leading-tight">{t.label}</TabsTrigger>)}
          </TabsList>
          {TABS.map((t) => (
            <TabsContent key={t.tipo} value={t.tipo} className="space-y-3 min-w-0">
              <div className="flex gap-2 min-w-0">
                <Input data-testid={`novo-${t.tipo}-input`} placeholder={`Novo item (${t.label})`} value={novo[t.tipo]} onChange={(e) => setNovo((s) => ({ ...s, [t.tipo]: e.target.value }))} className="min-w-0 flex-1" />
                <Button data-testid={`add-${t.tipo}-btn`} onClick={() => add(t.tipo)} disabled={!novo[t.tipo].trim()} className="shrink-0">Adicionar</Button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto overflow-x-hidden">
                {(t.tipo === "grau" ? [...items.grau].sort((a, b) => (a.hier ?? 99) - (b.hier ?? 99)) : items[t.tipo]).map((it, idx, arr) => (
                  <div key={it.id} data-testid={`lista-row-${it.id}`} className="border border-border rounded-md p-2.5 min-w-0">
                   <div className="flex items-center justify-between gap-2 min-w-0">
                    {editId === it.id ? (
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Input data-testid={`lista-edit-input-${it.id}`} value={editNome} onChange={(e) => setEditNome(e.target.value)} className="h-8 min-w-0 flex-1" />
                        <button data-testid={`lista-edit-save-${it.id}`} onClick={() => saveEdit(it)} className="text-emerald-400 hover:text-emerald-300 shrink-0"><Check className="h-4 w-4" /></button>
                        <button data-testid={`lista-edit-cancel-${it.id}`} onClick={cancelEdit} className="text-muted-foreground hover:text-foreground shrink-0"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <>
                        <span className={`min-w-0 flex-1 truncate text-sm ${it.ativo ? "" : "line-through text-muted-foreground"}`} title={it.nome}>{it.nome}</span>
                        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                          {t.tipo === "grau" && (
                            <>
                              <button data-testid={`grau-up-${it.id}`} onClick={() => moveGrau(arr, idx, -1)} disabled={idx === 0} className="text-muted-foreground hover:text-primary disabled:opacity-30" title="Subir na hierarquia"><ChevronUp className="h-4 w-4" /></button>
                              <button data-testid={`grau-down-${it.id}`} onClick={() => moveGrau(arr, idx, 1)} disabled={idx === arr.length - 1} className="text-muted-foreground hover:text-primary disabled:opacity-30" title="Descer na hierarquia"><ChevronDown className="h-4 w-4" /></button>
                              <button data-testid={`grau-alias-${it.id}`} onClick={() => startAlias(it)} className="text-muted-foreground hover:text-primary" title="Equivalências"><Tags className="h-3.5 w-3.5" /></button>
                            </>
                          )}
                          <button data-testid={`lista-edit-${it.id}`} onClick={() => startEdit(it)} className="text-muted-foreground hover:text-primary" title="Editar"><Pencil className="h-3.5 w-3.5" /></button>
                          <Switch data-testid={`lista-toggle-${it.id}`} checked={it.ativo} onCheckedChange={() => toggle(t.tipo, it)} title={it.ativo ? "Desativar" : "Ativar"} />
                          {isAdmin && (
                            <button data-testid={`lista-del-${it.id}`} onClick={() => setDelTarget({ id: it.id, nome: it.nome })} className="text-muted-foreground hover:text-red-400" title="Excluir definitivamente"><Trash2 className="h-3.5 w-3.5" /></button>
                          )}
                        </div>
                      </>
                    )}
                   </div>
                   {t.tipo === "grau" && aliasId === it.id && (
                     <div className="flex items-center gap-2 mt-2 min-w-0">
                       <Input data-testid={`grau-alias-input-${it.id}`} value={aliasText} onChange={(e) => setAliasText(e.target.value)} placeholder="Equivalências separadas por vírgula (ex.: 1º SGT PM, 1o SGT)" className="h-8 min-w-0 flex-1" />
                       <button data-testid={`grau-alias-save-${it.id}`} onClick={() => saveAlias(it)} className="text-emerald-400 hover:text-emerald-300 shrink-0"><Check className="h-4 w-4" /></button>
                       <button data-testid={`grau-alias-cancel-${it.id}`} onClick={() => { setAliasId(null); setAliasText(""); }} className="text-muted-foreground hover:text-foreground shrink-0"><X className="h-4 w-4" /></button>
                     </div>
                   )}
                   {t.tipo === "grau" && aliasId !== it.id && (it.aliases || []).length > 0 && (
                     <div className="mt-1.5 text-[11px] text-muted-foreground font-mono truncate" title={(it.aliases || []).join(", ")}>≡ {(it.aliases || []).join(", ")}</div>
                   )}
                  </div>
                ))}
                {!items[t.tipo].length && <p className="text-sm text-muted-foreground">Nenhum item.</p>}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>

      <AlertDialog open={!!delTarget} onOpenChange={(v) => { if (!v) setDelTarget(null); }}>
        <AlertDialogContent className="bg-popover border-border" data-testid="lista-del-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir definitivamente “{delTarget?.nome}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não poderá ser desfeita. O histórico já registrado (alunos/turmas anteriores) é preservado por snapshot; o item apenas sai do catálogo configurável.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lista-del-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="lista-del-confirmar" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">Excluir definitivamente</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
